import { useState, useRef } from 'react';
import useRecorder from './useRecorder';
import WebSocketManager from '../manager/WebSocketManager';
import { getBrowserName } from '../utils/helper';
import ConsoleUtils from '../utils/console';

let sourceData;
let audioBufferQueue = [];
const webSocketManager = new WebSocketManager();
let currentIndex = 0;

const getAudioContext = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) {
      setErrorMessage('Web Audio API is not supported in this browser.');
      handleStopRecording();
      throw new Error('Web Audio API is not supported in this browser.');
    }
    return new AudioContext();
  } catch (error) {
    ConsoleUtils.error('Failed to create AudioContext:', error);
    return null;
  }
};

const useAudioRecorder = () => {
  const audioContext = useRef(null);
  let isPlaying = useRef(false);
  const [isStartSession, setIsStartSession] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [resMessage, setResMessage] = useState();
  const [hideMicrophone, setHideMicrophone] = useState(false);
  const { handleStartRecording, handleStopRecording } = useRecorder({
    response,
  });
  let isLive = useRef(false);

  function init() {
    isLive.current = true;
    isPlaying.current = false;
    if (!audioContext.current) {
      audioContext.current = getAudioContext();
    }
    setResMessage('');
    fixAudioContext();
    handleStartRecording(false);
    setIsStartSession(true);
  }

  function fixAudioContext() {
    if (!audioContext.current) {
      audioContext.current = getAudioContext();
    }
    // Create empty buffer
    const buffer = audioContext.current.createBuffer(1, 1, 22050);
    const source = audioContext.current.createBufferSource();
    source.buffer = buffer;
    // Connect to output (speakers)
    source.connect(audioContext.current.destination);
    // Play sound
    if (source.start) {
      source.start(0);
    } else if (source.play) {
      source.play(0);
    } else if (source.noteOn) {
      source.noteOn(0);
    }
    // Cleanup event listeners to prevent memory leaks
    document.removeEventListener('touchstart', fixAudioContext);
    document.removeEventListener('touchend', fixAudioContext);
    // ConsoleUtils.log(`event remove ---`)
  }

  function response(type, event) {
    if (isLive.current) {
      if (type === 'startEvent') {
        setErrorMessage();
        webSocketManager.send({
          id: '5397b5bf-e413-4dbc-bf9d-47bb7b5fbf53',
          payloadType: 'event',
          type: 'startListening',
        });
      } else if (type === 'partialAudio') {
        webSocketManager.send({
          id: '5397b5bf-e413-4dbc-bf9d-47bb7b5fbf53',
          payloadType: 'message',
          type: 'audioBuffer',
          content: {
            audio: event,
          },
        });
      } else if (type === 'audioBuffer') {
        setResMessage(event?.content?.audio ? 'Listening...' : 'Talk to interupt');
        // ConsoleUtils.log('audioBuffer event call', event);
        speak(event?.content?.audio, event?.content?.audio ? false : true);
      } else if (type === 'endSession') {
        endSession();
      } else if (type === 'permissionDenied') {
        setErrorMessage(event);
        handleStopRecording();
      } else if (type === 'dictationEvent') {
        // ConsoleUtils.log('✅ Dictation message received.===============');
        if (isPlaying.current) resetAudioContext();
        setResMessage(event ?? '');
      } else if (type === 'dictationMessage') {
        setResMessage(event ?? '');
      } else if (type === 'stopListening') {
        setResMessage(event ?? '');
        isLive.current = false;
        isPlaying.current = false;
        setIsStartSession(false);
        resetAudioContext();
        audioContext.current = null;
      } else if (type === 'hideMicrophone') {
        setHideMicrophone(true);
      }
    } else {
      // ConsoleUtils.log(`=== Rejected response isLive  --[${isLive.current}]`)
    }
  }

  async function endSession() {
    // ConsoleUtils.log(`endSession ---isLive false`)
    isLive.current = false;
    isPlaying.current = false;
    handleStopRecording();
    resetAudioContext();
    setResMessage('');
    webSocketManager.send({
      id: '5397b5bf-e413-4dbc-bf9d-47bb7b5fbf53',
      payloadType: 'event',
      type: 'stopListening',
    });
    setIsStartSession(false);
    audioContext.current = null;
  }

  function startListen() {
    isPlaying.current = false;
    // ConsoleUtils.log(
    //   "Loadend Event in Speak Function: Assistant speaking stopped.."
    // );
    if (isLive.current) {
      try {
        setResMessage('Listening...');
      } catch (e) {
        ConsoleUtils.error('Start Listen:', e);
      }
    }
  }

  function speak(mp3Buffer, isFinal) {
    if (isLive.current) {
      // ConsoleUtils.log(`speak added isLive  --[${isLive.current}]`);
      audioBufferQueue.push({ mp3Buffer, isFinal, currentIndex });

      if (!isPlaying.current) {
        // ConsoleUtils.log('start player called');
        startPlayer();
      }
    } else {
      // ConsoleUtils.log(`=== Rejected speak response isLive  --[${isLive.current}]`);
    }
  }

  const startPlayer = async () => {
    // ConsoleUtils.log(`🔹 startPlayer() called.`);

    if (audioBufferQueue.length === 0) {
      // ConsoleUtils.log(`🛑 Queue is empty. Stopping player.`);
      isPlaying.current = false;
      return;
    }

    const { mp3Buffer, isFinal } = audioBufferQueue.shift();

    if (isFinal) {
      // ConsoleUtils.log(`✅ Final buffer received. Stopping player.`);
      startListen();
      return;
    }

    isPlaying.current = true;

    if (!audioContext.current) {
      // ConsoleUtils.log('🎵 Initializing AudioContext...');
      audioContext.current = getAudioContext();
    }

    // ✅ Ensure AudioContext is active
    if (audioContext?.current && audioContext?.current?.state === 'suspended') {
      await audioContext.current.resume();
      // ConsoleUtils.log(`🔄 AudioContext resumed.`);
    }

    if (!mp3Buffer || mp3Buffer.length === 0) {
      // ConsoleUtils.error(`❌ Invalid mp3Buffer, skipping...`);
      startPlayer();
      return;
    }

    try {
      const audioData = Uint8Array.from(atob(mp3Buffer), (c) => c.charCodeAt(0));
      audioContext?.current
        .decodeAudioData(audioData.buffer)
        .then((audioBuffer) => {
          if (!audioBuffer || audioBuffer.duration === 0) {
            // ConsoleUtils.log(`❌ Decoded audioBuffer is EMPTY! Skipping...`);
            startPlayer();
            return;
          }

          // ConsoleUtils.log(`✅ Decoded audio buffer duration: ${audioBuffer.duration} seconds`);

          // ✅ Ensure previous sourceData is stopped before creating a new one
          if (sourceData) {
            // ConsoleUtils.log(`🔴 Stopping previous sourceData.`);
            sourceData.stop();
          }

          sourceData = audioContext.current.createBufferSource();
          sourceData.buffer = audioBuffer;
          sourceData.connect(audioContext.current.destination);

          // ✅ Ensure `onended` fires
          sourceData.onended = () => {
            // ConsoleUtils.log(`🔚 Playback finished. Playing next...`);
            sourceData = null; // Prevent memory leaks
            startPlayer(); // Small delay before next play
          };

          // ConsoleUtils.log(`▶ Starting audio playback...`);
          sourceData.start(0);
          // ConsoleUtils.log(`✅ Audio playback started.`);
        })
        .catch((error) => {
          // ConsoleUtils.log(`❌ Error decoding audio:`, error);
          startPlayer();
        });
    } catch (error) {
      // ConsoleUtils.log(`❌ Critical error in startPlayer:`, error);
      startPlayer();
    }
  };

  const resetAudioContext = () => {
    // ConsoleUtils.log(`[${new Date().toISOString()}] 🛑 Stopping audio playback globally.`);

    // Stop current playing audio
    if (sourceData) {
      try {
        sourceData.stop();
        sourceData.disconnect();
        // ConsoleUtils.log('🔴 Audio source stopped.');
      } catch (error) {
        ConsoleUtils.error('❌ Error stopping audio source:', error);
      }
      sourceData = null;
    }

    // Clear the queue
    audioBufferQueue = [];
    // ConsoleUtils.log('🗑️ Audio buffer queue cleared.');

    // Reset play states
    isPlaying.current = false;

    // Optionally, reset the AudioContext for a clean restart
    if (audioContext.current) {
      try {
        audioContext.current.close();
        audioContext.current = new (window.AudioContext || window.webkitAudioContext)();
        // ConsoleUtils.log('🔄 AudioContext reset.');
      } catch (error) {
        ConsoleUtils.error('❌ Error resetting AudioContext:', error);
      }
    }
  };

  return {
    init,
    endSession,
    resMessage,
    errorMessage,
    isStartSession,
    setIsStartSession,
    hideMicrophone,
    startPlayer,
  };
};

export default useAudioRecorder;
